/**
 * The agent-entered consent link: the URL the customer clicks, and the limits the flow runs under.
 *
 * Mirrors `advisorEmailVerifyLink` (fragment-based token, scrubbed by the page on first paint) but
 * stays separate: this token is not a credential for any account and signs nobody in.
 */

import { getBaseUrl } from './baseUrl';

/** Must be a PUBLIC route: the customer arrives signed out and may have no account at all. */
export const AGENT_ENTERED_CONSENT_PATH = '/consent/agent-entered';

/** Deliberately distinct from any sign-in ticket name, so nothing can mistake one for the other. */
export const AGENT_ENTERED_CONSENT_TOKEN_PARAM = 'consent_token';

/** Long, because the recipient did not ask for this email and an expiry costs them a round trip. */
export const AGENT_ENTERED_CONSENT_EXPIRY_DAYS = 14;
const AGENT_ENTERED_CONSENT_EXPIRY_MS = AGENT_ENTERED_CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

/**
 * Counted per REQUEST, not per IP: a date of birth has ~36k plausible values, so the limit has to
 * bind to the thing being guessed at. Attackers rotate IPs; real customers share them via CGNAT.
 */
export const AGENT_ENTERED_CONSENT_MAX_ATTEMPTS = 5;

/** How long a request stays locked after the attempt limit is reached. */
export const AGENT_ENTERED_CONSENT_LOCKOUT_MINUTES = 15;
const AGENT_ENTERED_CONSENT_LOCKOUT_MS = AGENT_ENTERED_CONSENT_LOCKOUT_MINUTES * 60 * 1000;

/** Minimum gap between sends for one request. */
export const AGENT_ENTERED_CONSENT_RESEND_COOLDOWN_MINUTES = 60;
const AGENT_ENTERED_CONSENT_RESEND_COOLDOWN_MS =
  AGENT_ENTERED_CONSENT_RESEND_COOLDOWN_MINUTES * 60 * 1000;

/** Total sends allowed for one request, initial send included. */
export const AGENT_ENTERED_CONSENT_MAX_SENDS = 5;

/**
 * OPEN QUESTION FOR NEW YORK LIFE. Zero means per-case. Raising it lets an agent enter information
 * under permission given for a different case, so it needs a written answer first.
 */
export const AGENT_ENTERED_CONSENT_REUSE_DAYS = 0;

export function agentEnteredConsentLink({ token }: { token: string }): string {
  // FRAGMENT, never the query string: fragments are not sent to servers, so the token stays out
  // of access logs, proxy logs and Referer headers.
  const fragment = new URLSearchParams({ [AGENT_ENTERED_CONSENT_TOKEN_PARAM]: token });
  return `${getBaseUrl()}${AGENT_ENTERED_CONSENT_PATH}#${fragment.toString()}`;
}

/** The expiry instant for a link minted now. */
export function agentEnteredConsentExpiry(fromMs: number = Date.now()): Date {
  return new Date(fromMs + AGENT_ENTERED_CONSENT_EXPIRY_MS);
}

/** The instant a request locked now stays locked until. */
export function agentEnteredConsentLockoutUntil(fromMs: number = Date.now()): Date {
  return new Date(fromMs + AGENT_ENTERED_CONSENT_LOCKOUT_MS);
}

/** Whether a resend is allowed yet, given when the last one went out. */
export function canResendAgentEnteredConsent(
  lastSentDateTime: Date | null | undefined,
  fromMs: number = Date.now(),
): boolean {
  if (!lastSentDateTime) return true;
  return fromMs - lastSentDateTime.getTime() >= AGENT_ENTERED_CONSENT_RESEND_COOLDOWN_MS;
}

/** When a resend becomes allowed, or null when one is allowed now. */
export function agentEnteredConsentResendAvailableAt(
  lastSentDateTime: Date | null | undefined,
  fromMs: number = Date.now(),
): Date | null {
  if (canResendAgentEnteredConsent(lastSentDateTime, fromMs)) return null;
  return new Date(lastSentDateTime!.getTime() + AGENT_ENTERED_CONSENT_RESEND_COOLDOWN_MS);
}

/**
 * For anywhere a consent email body is stored, logged or replayed. A live token is a one-shot
 * authority to consent on the customer's behalf.
 */
export function redactAgentEnteredConsentLinks(text: string): string {
  const pattern = new RegExp(`(${AGENT_ENTERED_CONSENT_TOKEN_PARAM}=)[A-Za-z0-9._-]+`, 'g');
  return text.replace(pattern, '$1[redacted]');
}
